import LinkGroup from 'components/elements/form/LinkGroup'
import AccidentIcon from 'components/elements/icons/AccidentIcon'
import EmptyFuelIcon from 'components/elements/icons/EmptyFuelIcon'
import EngineTroubleIcon from 'components/elements/icons/EngineTroubleIcon'
import FallingWheelIcon from 'components/elements/icons/FallingWheelIcon'
import FlatTireIcon from 'components/elements/icons/FlatTireIcon'
import LossKeyIcon from 'components/elements/icons/LossKeyIcon'
import OthersIcon from 'components/elements/icons/OthersIcon'
import RequestTemplate from 'components/templates/RequestTemplate'
import { PAGE_PATH } from 'constants/pagePath'
import {
  RESERVATION_DATE_FORMAT,
  RESERVATION_DATE_MAX,
  VEHICLE_STATE,
  VEHICLE_STATE_FORM_EARLIER_MINTIME,
  VEHICLE_STATE_FORM_EARLIER_FORMAT,
} from 'constants/qnState'
import { EditingItemContext } from 'contexts/EditingItemContext'
import React, { useContext, useEffect } from 'react'
import { QnStatesProps } from 'states/QnState'
import { StatesProps } from 'utils/commonHooks'
import { StatusProps, success } from 'utils/status'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import { LinkLineProps } from 'components/elements/items/LinkLine'
import { OptionItem } from 'components/elements/common'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import { TroubleDatetimeTypes } from 'states/QnState'
import dayjs from 'dayjs'
import { useProgressPoint } from '../../../../utils/commonHooks'
import { initialQnEditState } from '../../../../states/QnState'

/**
 * QN-03: お車の状況
 */
const VehicleState: React.FC<StatesProps> = (props: QnStatesProps) => {
  const [editingItem, setEditingItem] = useContext(EditingItemContext)
  const { state, setState } = props
  const currentTime = dayjs().hour()
  const AM_LIST = ['todayNow', 'yesterdayPM', 'yesterdayAM', 'earlier']
  const PM_LIST = ['justNow', 'todayAM', 'yesterdayPM', 'earlier']

  /** トラブル発生日時の選択肢 */
  const troubleDatetimeOptions = React.useMemo(() => {
    return Object.entries(VEHICLE_STATE_FORM.troubleDatetime.options)
      .filter((x) => {
        return currentTime >= 12
          ? PM_LIST.includes(x[0])
          : AM_LIST.includes(x[0])
      })
      .map(
        ([k, v]) =>
          ({
            label: v as TroubleDatetimeTypes,
            value: k,
          } as OptionItem<TroubleDatetimeTypes>)
      )
  }, [currentTime])

  /** 午前午後切り替え処理 */
  useEffect(() => {
    const hasTroubleDateTime = troubleDatetimeOptions.some(
      (x) => x.value === editingItem.troubleDatetime.timing
    )
    setEditingItem({
      ...editingItem,
      troubleDatetime: {
        timing: hasTroubleDateTime
          ? editingItem.troubleDatetime.timing
          : currentTime >= 12
          ? 'justNow'
          : 'todayNow',
        date: editingItem.troubleDatetime.date,
      },
    })
  }, [])

  /** トラブル発生日時選択変更時のコールバック */
  const handleTroubleDatetimeChanged = React.useCallback(
    (value: TroubleDatetimeTypes): void => {
      setEditingItem({
        ...editingItem,
        troubleDatetime: {
          timing: value,
          date: editingItem.troubleDatetime.date,
        },
      })
    },
    [setEditingItem]
  )

  /** リンク選択による遷移時に実行されるアクションのコールバック */
  const handleWithAction = React.useCallback(
    (newState: string): StatusProps => {
      setEditingItem({
        ...editingItem,
        troubleDatetime: editingItem.troubleDatetime,
        vehicleState: newState ?? '',
      })
      return success()
    },
    [editingItem.troubleDatetime]
  )

  /** 各トラブル入力画面へのリンクを表示するための配列データ */
  const links: LinkLineProps[] = React.useMemo(
    () => [
      {
        icon: <EngineTroubleIcon />,
        forwardTo: PAGE_PATH.ENGINE_TROUBLE,
        label: VEHICLE_STATE.ENGINE_TROUBLE,
        withAction: (): StatusProps =>
          handleWithAction(VEHICLE_STATE.ENGINE_TROUBLE),
      },
      {
        icon: <FlatTireIcon />,
        forwardTo: PAGE_PATH.FLAT_TIRE,
        label: VEHICLE_STATE.FLAT_TIRE,
        withAction: (): StatusProps =>
          handleWithAction(VEHICLE_STATE.FLAT_TIRE),
      },
      {
        icon: <AccidentIcon />,
        forwardTo: PAGE_PATH.ACCIDENT,
        label: VEHICLE_STATE.ACCIDENT,
        withAction: (): StatusProps => handleWithAction(VEHICLE_STATE.ACCIDENT),
      },
      {
        icon: <FallingWheelIcon />,
        forwardTo: PAGE_PATH.FALLING_WHEEL,
        label: VEHICLE_STATE.FALLING_WHEEL,
        withAction: (): StatusProps =>
          handleWithAction(VEHICLE_STATE.FALLING_WHEEL),
      },
      {
        icon: <EmptyFuelIcon />,
        forwardTo: PAGE_PATH.EMPTY_FUEL,
        label: VEHICLE_STATE.EMPTY_FUEL,
        withAction: (): StatusProps =>
          handleWithAction(VEHICLE_STATE.EMPTY_FUEL),
      },
      {
        icon: <LossKeyIcon />,
        forwardTo: PAGE_PATH.LOSS_KEY,
        label: VEHICLE_STATE.LOSS_KEY,
        withAction: (): StatusProps => handleWithAction(VEHICLE_STATE.LOSS_KEY),
      },
      {
        icon: <OthersIcon />,
        forwardTo: PAGE_PATH.OTHERS,
        label: VEHICLE_STATE.OTHERS,
        withAction: (): StatusProps => handleWithAction(VEHICLE_STATE.OTHERS),
      },
    ],
    [handleWithAction]
  )

  /** 戻り先 */
  const backTo = React.useMemo((): string => {
    const { date, time } = editingItem.reservation
    const isInputReservation =
      date && time ? PAGE_PATH.RESERVATION : PAGE_PATH.START
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : isInputReservation
  }, [])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    if (state.confirmationChangeType) {
      setEditingItem({
        ...editingItem,
        troubleDatetime: state.troubleDatetime,
        vehicleState: state.vehicleState,
      })
    } else {
      setState({
        ...state,
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: initialQnEditState.vehicleState ?? '',
      })
      setEditingItem({
        ...editingItem,
        troubleDatetime: initialQnEditState.troubleDatetime,
        vehicleState: initialQnEditState.vehicleState,
      })
    }
    return success()
  }, [state.confirmationChangeType, state.troubleDatetime, state.vehicleState])

  return (
    <RequestTemplate
      title={'お車の状況'}
      showContact
      progress={useProgressPoint()}
      content={
        <React.Fragment>
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.troubleDatetime.formName}`}
            isRequired={true}
            options={troubleDatetimeOptions}
            selectedValue={editingItem.troubleDatetime.timing}
            onValueChanged={handleTroubleDatetimeChanged}
          />
          <LinkGroup title={'お車の状況を選択'} links={links} />
        </React.Fragment>
      }
      hideForward
      showBack
      backTo={backTo}
      backAction={backAction}
      notClearEditingItem
    />
  )
}

export default VehicleState
